import { memo, useEffect, useState } from 'react';

import cn from 'classnames';
import { ToastContainer } from 'react-toastify';
import { useShallow } from 'zustand/react/shallow';

import { Badge, Box, IconButton, Stack, Typography } from '@mui/material';

import { BsCameraVideo, BsCameraVideoOffFill, BsMic, BsMicMuteFill } from 'react-icons/bs';
import { IoAlert } from 'react-icons/io5';
import { MdWaves } from 'react-icons/md';

import { OButton, OInput } from '@src/shared';
import { AvatarCircle } from '@src/components';

import { isWebView } from '@src/helpers';
import { sendEventToWebView } from '@src/bridge/bridgeMessenger';

import { useDrawerStore } from '@src/stores';
import { useWebinarLobby } from '@pages/Webinar/hooks';

import { LoaderIcon } from '@src/assets/icons';
import { EWebViewEvents } from '@src/bridge/types';

import { formatDateWithCity } from '@src/utils';

import styles from './WebinarLobbyMobile.module.scss';

const date = new Date();

const WebinarLobbyMobile = () => {
  const [isComponentReady, setIsComponentReady] = useState(false);

  const {
    roomTitle,
    isHost,
    isPasswordRequired,
    isWebinarStartedByHost,
    errors,
    password,
    peerName,
    inputValue,
    joinErrors,
    originalStream,
    isAudioAllowed,
    isVideoAllowed,
    handleAudio,
    handleVideo,
    handleChangeUsername,
    handleChangePassword,
    setVideoRef,
    isSubmitButtonDisabled,
    handleJoinWebinar,
    isMicClosed,
    isCamClosed,
    isCamDisabled,
    isMicDisabled,
    isJoinLoading,
  } = useWebinarLobby();

  const { setSelectedKey } = useDrawerStore(
    useShallow((state) => ({
      setSelectedKey: state.setSelectedKey,
    }))
  );

  // Mark component as ready after initial render
  useEffect(() => {
    setIsComponentReady(true);
  }, []);

  // Send page finished event to Android when WebinarLobbyMobile is fully rendered and not loading
  useEffect(() => {
    if (isComponentReady && !isJoinLoading) {
      sendEventToWebView({ eventType: EWebViewEvents.PAGE_FINISHED });
    }
  }, [isComponentReady, isJoinLoading]);

  return (
    <Stack className={styles.mediaPreviewMobile}>
      {!isWebView() && (
        <Stack className={styles.webinarHeader}>
          <Typography fontSize={18} className={styles.webinarHeaderTitle}>
            {roomTitle ?? 'Join the webinar'}
          </Typography>
        </Stack>
      )}

      <Stack className={styles.userPreviewContainer}>
        <Box className={styles.userPreview}>
          {originalStream && !isCamClosed ? (
            <video className={styles.video} ref={setVideoRef} autoPlay muted playsInline />
          ) : (
            <div className={styles.avatar}>
              <AvatarCircle name={peerName || 'Guest'} />
            </div>
          )}

          <Box className={styles.buttonsContainer}>
            {/* Mic Control */}
            <Box
              onClick={handleAudio}
              className={cn(styles.iconsContainer, {
                [styles.iconsContainerActive]: !isMicClosed,
                [styles.iconsContainerDisabled]: isMicDisabled && !isWebView(),
              })}
              style={isCamDisabled ? { pointerEvents: 'none', opacity: 0.5 } : {}}
            >
              {!isMicClosed ? (
                <BsMic className={styles.iconsContainerIcon} />
              ) : (
                <BsMicMuteFill className={styles.iconsContainerIcon} />
              )}
              <Badge
                badgeContent={<IoAlert size={14} />}
                classes={{ badge: styles.badge }}
                invisible={isAudioAllowed}
              />
            </Box>

            {/* Camera Control */}
            <Box
              onClick={handleVideo}
              className={cn(styles.iconsContainer, {
                [styles.iconsContainerActive]: !isCamClosed,
                [styles.iconsContainerDisabled]: !isCamDisabled && !isWebView(),
              })}
              style={isCamDisabled ? { pointerEvents: 'none', opacity: 0.5 } : {}}
            >
              {!isCamClosed ? (
                <BsCameraVideo className={styles.iconsContainerIcon} />
              ) : (
                <BsCameraVideoOffFill className={styles.iconsContainerIcon} />
              )}
              <Badge
                badgeContent={<IoAlert size={14} />}
                classes={{ badge: styles.badge }}
                invisible={isVideoAllowed}
              />
            </Box>

            {/* Virtual Background */}
            <IconButton
              className={styles.virtualBackgroundWrapper}
              onClick={() => setSelectedKey('virtual-background')}
            >
              <MdWaves className={styles.virtualBackgroundIcon} />
            </IconButton>
          </Box>
        </Box>
      </Stack>

      <Stack className={styles.userNameContainer}>
        {isWebinarStartedByHost || isHost ? (
          <>
            <Box>
              <OInput
                className={styles.userNameInput}
                placeholder="Enter your Nickname"
                type="text"
                value={inputValue}
                onChange={(e) => handleChangeUsername(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleJoinWebinar();
                  }
                }}
                invalid={!!errors.username}
              />
              {errors && <span className={styles.invalid}>{errors.username}</span>}
            </Box>

            {isPasswordRequired && (
              <Box>
                <OInput
                  className={styles.passwordInput}
                  placeholder="Password"
                  type="password"
                  value={password}
                  onChange={handleChangePassword}
                  invalid={!!joinErrors.password}
                />
                {joinErrors.password && <span className={styles.invalid}>{joinErrors.password}</span>}
              </Box>
            )}

            <OButton
              variant="alternative"
              size="large"
              text="Join meeting"
              loading={isJoinLoading}
              isDisabled={isSubmitButtonDisabled || (isPasswordRequired && !password) || isJoinLoading}
              onClick={handleJoinWebinar}
              style={{ width: '100%' }}
            />
          </>
        ) : (
          <Box className={styles.webinarDetails}>
            <Typography className={styles.webinarTitle}>v webinar meeting</Typography>
            <Typography className={styles.webinarDate}>{formatDateWithCity(date)}</Typography>
            <Box className={styles.waitingForHostWrapper}>
              <Typography fontSize={16}>Waiting for host to start the meeting</Typography>
              <Box className={styles.loaderWrapper}>
                <LoaderIcon width={29} height={29} />
              </Box>
            </Box>
          </Box>
        )}
      </Stack>
      <ToastContainer position="top-right" autoClose={3500} />
    </Stack>
  );
};

export default memo(WebinarLobbyMobile);
